'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function BodyFatCalculator() {
  const t = useTranslations("tools.bodyFatCalculator");
  const [screenSize, setScreenSize] = useState('mobile');

  // Screen size detection
  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  // Calculator state
  const [unitSystem, setUnitSystem] = useState<'imperial' | 'metric'>('imperial');
  const [gender, setGender] = useState<'male' | 'female'>('male');
  const [inputs, setInputs] = useState({
    age: 25,
    // Imperial
    weightLbs: 152,
    heightFt: 5,
    heightIn: 10.5,
    neckIn: 15.5,
    waistIn: 37.5,
    hipIn: 34.5,
    // Metric
    weightKg: 70,
    heightCm: 178,
    neckCm: 39,
    waistCm: 96,
    hipCm: 88,
  });
  const [results, setResults] = useState<any>(null);

  // Responsive style helper
  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
  };

  const styles = {
    container: getResponsiveStyle(
      { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
      { padding: '1rem' },
      { padding: '1.5rem' },
      { padding: '2rem' }
    ),
    header: getResponsiveStyle(
      { textAlign: 'center' as const, marginBottom: '2rem' },
      {},
      { marginBottom: '2.5rem' },
      { marginBottom: '3rem' }
    ),
    h1: getResponsiveStyle(
      { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2 },
      { fontSize: '2rem' },
      { fontSize: '2.5rem' },
      { fontSize: '3rem' }
    ),
    summaryText: getResponsiveStyle(
      { color: colors.textSecondary, lineHeight: 1.6, maxWidth: '800px', margin: '0 auto' },
      { fontSize: '1rem' },
      { fontSize: '1.125rem' },
      { fontSize: '1.25rem' }
    ),
    calculatorTool: getResponsiveStyle(
      {
        background: 'linear-gradient(135deg, rgba(240, 249, 255, 0.9) 0%, rgba(224, 242, 254, 0.9) 100%)',
        borderRadius: '1rem',
        marginBottom: '2rem',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
      },
      { padding: '1.5rem 1rem' },
      { padding: '2rem 1.5rem', borderRadius: '1.25rem' },
      { padding: '3rem 2rem', borderRadius: '1.5rem' }
    ),
    toolContainer: getResponsiveStyle(
      { maxWidth: '100%', margin: '0 auto' },
      {},
      { maxWidth: '700px' },
      { maxWidth: '900px' }
    ),
    toolHeader: getResponsiveStyle(
      { textAlign: 'center' as const, marginBottom: '1.5rem' },
      {},
      { marginBottom: '2rem' },
      {}
    ),
    h2: getResponsiveStyle(
      { fontWeight: 600, color: colors.textPrimary, marginBottom: '1rem' },
      { fontSize: '1.5rem' },
      { fontSize: '1.75rem' },
      { fontSize: '2rem' }
    ),
    calculatorForm: getResponsiveStyle(
      {
        background: 'white',
        borderRadius: '1rem',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
        marginBottom: '1.5rem',
      },
      { padding: '1.5rem' },
      { padding: '2rem' },
      { padding: '2rem' }
    ),
    unitTabs: {
      display: 'flex',
      gap: '0.5rem',
      marginBottom: '2rem',
      borderBottom: '2px solid #e2e8f0',
    },
    unitTab: (active: boolean) => getResponsiveStyle(
      {
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        color: active ? '#2563eb' : '#64748b',
        borderBottom: active ? '3px solid #2563eb' : '3px solid transparent',
        fontWeight: active ? 600 : 400,
        transition: 'all 0.3s',
      },
      { padding: '0.75rem 1rem', fontSize: '0.9rem' },
      { padding: '0.75rem 1.5rem', fontSize: '1rem' },
      { padding: '0.75rem 1.5rem', fontSize: '1rem' }
    ),
    formRow: getResponsiveStyle(
      {
        display: 'grid',
        gap: '1.5rem',
        marginBottom: '1.5rem',
      },
      { gridTemplateColumns: '1fr' },
      { gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))' },
      { gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))' }
    ),
    formGroup: {
      display: 'flex',
      flexDirection: 'column' as const,
    },
    label: getResponsiveStyle(
      { fontWeight: 600, color: '#334155', marginBottom: '0.5rem' },
      { fontSize: '0.9rem' },
      { fontSize: '0.95rem' },
      { fontSize: '0.95rem' }
    ),
    radioGroup: {
      display: 'flex',
      gap: '1.5rem',
      marginTop: '0.5rem',
    },
    radioLabel: {
      display: 'flex',
      alignItems: 'center',
      cursor: 'pointer',
      fontWeight: 400,
    },
    radio: {
      marginRight: '0.5rem',
      width: '18px',
      height: '18px',
      cursor: 'pointer',
    },
    inputWrapper: {
      display: 'flex',
      gap: '0.5rem',
    },
    input: getResponsiveStyle(
      {
        padding: '0.75rem',
        border: '2px solid #e2e8f0',
        borderRadius: '0.5rem',
        transition: 'border-color 0.3s',
        flex: 1,
        outline: 'none',
      },
      { fontSize: '1rem' },
      { fontSize: '1rem' },
      { fontSize: '1rem' }
    ),
    unitLabel: getResponsiveStyle(
      {
        display: 'flex',
        alignItems: 'center',
        padding: '0.75rem',
        background: '#f1f5f9',
        borderRadius: '0.5rem',
        color: '#64748b',
        minWidth: '60px',
        justifyContent: 'center',
      },
      { fontSize: '0.85rem' },
      { fontSize: '0.9rem' },
      { fontSize: '0.9rem' }
    ),
    calculateButton: getResponsiveStyle(
      {
        width: '100%',
        fontWeight: 700,
        color: 'white',
        background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
        border: 'none',
        borderRadius: '0.5rem',
        cursor: 'pointer',
        transition: 'transform 0.2s, box-shadow 0.2s',
        marginTop: '1rem',
      },
      { padding: '0.875rem 1.5rem', fontSize: '1rem' },
      { padding: '1rem 2rem', fontSize: '1.125rem' },
      { padding: '1rem 2rem', fontSize: '1.125rem' }
    ),
    results: getResponsiveStyle(
      {
        background: 'white',
        borderRadius: '1rem',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
      },
      { padding: '1.5rem' },
      { padding: '2rem' },
      { padding: '2rem' }
    ),
    primaryResult: getResponsiveStyle(
      {
        textAlign: 'center' as const,
        background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
        borderRadius: '0.75rem',
        marginBottom: '2rem',
      },
      { padding: '1.5rem' },
      { padding: '2rem' },
      { padding: '2rem' }
    ),
    percentage: getResponsiveStyle(
      { fontWeight: 700, color: '#2563eb', marginBottom: '0.5rem' },
      { fontSize: '2.5rem' },
      { fontSize: '3rem' },
      { fontSize: '3rem' }
    ),
    category: getResponsiveStyle(
      { color: '#64748b', fontWeight: 500 },
      { fontSize: '1.1rem' },
      { fontSize: '1.25rem' },
      { fontSize: '1.25rem' }
    ),
    bodyFatChart: {
      margin: '2rem 0',
      padding: '1rem',
      background: '#f8fafc',
      borderRadius: '0.75rem',
    },
    chartBar: {
      height: '40px',
      background: 'linear-gradient(90deg, #22c55e 0%, #eab308 50%, #ef4444 100%)',
      borderRadius: '20px',
      position: 'relative' as const,
      margin: '1rem 0',
    },
    chartMarker: (position: number) => ({
      position: 'absolute' as const,
      top: '-10px',
      left: `${position}%`,
      transform: 'translateX(-50%)',
      width: 0,
      height: 0,
      borderLeft: '10px solid transparent',
      borderRight: '10px solid transparent',
      borderTop: '15px solid #1e293b',
    }),
    chartLabels: getResponsiveStyle(
      {
        display: 'flex',
        justifyContent: 'space-between',
        color: '#64748b',
        marginTop: '0.5rem',
      },
      { fontSize: '0.75rem' },
      { fontSize: '0.85rem' },
      { fontSize: '0.85rem' }
    ),
    resultGrid: getResponsiveStyle(
      {
        display: 'grid',
        gap: '1rem',
        marginTop: '1.5rem',
      },
      { gridTemplateColumns: '1fr' },
      { gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))' },
      { gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))' }
    ),
    resultItem: {
      padding: '1rem',
      background: '#f8fafc',
      borderRadius: '0.5rem',
      borderLeft: '4px solid #2563eb',
    },
    resultLabel: getResponsiveStyle(
      { display: 'block', color: '#64748b', marginBottom: '0.25rem' },
      { fontSize: '0.85rem' },
      { fontSize: '0.9rem' },
      { fontSize: '0.9rem' }
    ),
    resultValue: getResponsiveStyle(
      { display: 'block', fontWeight: 700, color: '#1e293b' },
      { fontSize: '1.1rem' },
      { fontSize: '1.25rem' },
      { fontSize: '1.25rem' }
    ),
    referenceTable: {
      marginTop: '2rem',
      overflowX: 'auto' as const,
    },
    table: getResponsiveStyle(
      {
        width: '100%',
        borderCollapse: 'collapse' as const,
      },
      { fontSize: '0.85rem' },
      { fontSize: '0.9rem' },
      { fontSize: '0.9rem' }
    ),
    th: {
      background: '#f1f5f9',
      padding: '0.75rem',
      textAlign: 'left' as const,
      fontWeight: 600,
      color: '#334155',
      borderBottom: '2px solid #e2e8f0',
    },
    td: {
      padding: '0.75rem',
      borderBottom: '1px solid #e2e8f0',
      color: '#475569',
    },
    glassCard: getResponsiveStyle(
      {
        background: 'rgba(255, 255, 255, 0.6)',
        backdropFilter: 'blur(20px) saturate(180%)',
        WebkitBackdropFilter: 'blur(20px) saturate(180%)',
        border: '1px solid rgba(255, 200, 180, 0.2)',
        borderRadius: '1rem',
        boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
        marginBottom: '2rem',
      },
      { padding: '1.5rem' },
      { padding: '2rem' },
      { padding: '2.5rem' }
    ),
    paragraph: getResponsiveStyle(
      { lineHeight: 1.6, color: colors.textSecondary, marginBottom: '1rem' },
      { fontSize: '1rem' },
      { fontSize: '1.125rem' },
      { fontSize: '1.125rem' }
    ),
    list: getResponsiveStyle(
      { lineHeight: 1.8, color: colors.textSecondary, paddingLeft: '1.5rem', marginBottom: '1rem' },
      { fontSize: '1rem' },
      { fontSize: '1.125rem' },
      { fontSize: '1.125rem' }
    ),
    listItem: {
      marginBottom: '0.75rem',
    },
    faqItem: {
      marginBottom: '1.5rem',
    },
    h3: getResponsiveStyle(
      { fontWeight: 600, color: colors.textPrimary, marginBottom: '0.75rem' },
      { fontSize: '1.25rem' },
      { fontSize: '1.375rem' },
      { fontSize: '1.5rem' }
    ),
    reference: {
      marginBottom: '0.75rem',
    },
    referenceLink: {
      color: colors.primary,
      textDecoration: 'none',
      transition: 'color 0.3s',
    },
  };

  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();

    let heightCm: number, weightKg: number, neckCm: number, waistCm: number, hipCm: number;

    if (unitSystem === 'imperial') {
      heightCm = ((inputs.heightFt * 12) + inputs.heightIn) * 2.54;
      weightKg = inputs.weightLbs * 0.453592;
      neckCm = inputs.neckIn * 2.54;
      waistCm = inputs.waistIn * 2.54;
      hipCm = inputs.hipIn * 2.54;
    } else {
      heightCm = inputs.heightCm;
      weightKg = inputs.weightKg;
      neckCm = inputs.neckCm;
      waistCm = inputs.waistCm;
      hipCm = inputs.hipCm;
    }

    let bodyFatPercentage: number;
    if (gender === 'male') {
      bodyFatPercentage = 495 / (1.0324 - 0.19077 * Math.log10(waistCm - neckCm) + 0.15456 * Math.log10(heightCm)) - 450;
    } else {
      bodyFatPercentage = 495 / (1.29579 - 0.35004 * Math.log10(waistCm + hipCm - neckCm) + 0.22100 * Math.log10(heightCm)) - 450;
    }

    const bmi = weightKg / Math.pow(heightCm / 100, 2);
    const bodyFatMassKg = (bodyFatPercentage / 100) * weightKg;
    const leanBodyMassKg = weightKg - bodyFatMassKg;

    const idealBF = getIdealBodyFat(inputs.age, gender);
    const fatToLoseKg = Math.max(0, bodyFatMassKg - (idealBF / 100) * weightKg);

    const category = getBodyFatCategory(bodyFatPercentage, gender);
    const markerPosition = calculateMarkerPosition(bodyFatPercentage, gender);

    setResults({
      bodyFatPercentage,
      category,
      bmi,
      bodyFatMassKg,
      leanBodyMassKg,
      idealBF,
      fatToLoseKg,
      markerPosition,
    });
  };

  const getBodyFatCategory = (bf: number, gender: string): string => {
    if (gender === 'male') {
      if (bf < 6) return t('categoryEssentialMale');
      if (bf < 14) return t('categoryAthletes');
      if (bf < 18) return t('categoryFitness');
      if (bf < 25) return t('categoryAverage');
      return t('categoryObese');
    } else {
      if (bf < 14) return t('categoryEssentialFemale');
      if (bf < 21) return t('categoryAthletes');
      if (bf < 25) return t('categoryFitness');
      if (bf < 32) return t('categoryAverage');
      return t('categoryObese');
    }
  };

  const getIdealBodyFat = (age: number, gender: string): number => {
    if (gender === 'male') {
      if (age <= 20) return 8.5;
      if (age <= 25) return 10.5;
      if (age <= 30) return 12.7;
      if (age <= 35) return 13.7;
      if (age <= 40) return 15.3;
      if (age <= 45) return 16.4;
      if (age <= 50) return 18.9;
      return 20.9;
    } else {
      if (age <= 20) return 17.7;
      if (age <= 25) return 18.4;
      if (age <= 30) return 19.3;
      if (age <= 35) return 21.5;
      if (age <= 40) return 22.2;
      if (age <= 45) return 22.9;
      if (age <= 50) return 25.2;
      return 26.3;
    }
  };

  const calculateMarkerPosition = (bf: number, gender: string): number => {
    let position: number;
    if (gender === 'male') {
      if (bf < 2) position = 0;
      else if (bf < 6) position = (bf - 2) / 4 * 20;
      else if (bf < 14) position = 20 + (bf - 6) / 8 * 20;
      else if (bf < 18) position = 40 + (bf - 14) / 4 * 20;
      else if (bf < 25) position = 60 + (bf - 18) / 7 * 20;
      else if (bf < 35) position = 80 + (bf - 25) / 10 * 20;
      else position = 100;
    } else {
      if (bf < 10) position = 0;
      else if (bf < 14) position = (bf - 10) / 4 * 20;
      else if (bf < 21) position = 20 + (bf - 14) / 7 * 20;
      else if (bf < 25) position = 40 + (bf - 21) / 4 * 20;
      else if (bf < 32) position = 60 + (bf - 25) / 7 * 20;
      else if (bf < 42) position = 80 + (bf - 32) / 10 * 20;
      else position = 100;
    }
    return Math.min(100, Math.max(0, position));
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <header style={styles.header}>
        <h1 style={styles.h1}>{t('title')}</h1>
        <p style={styles.summaryText}>{t('subtitle')}</p>
      </header>

      {/* Calculator Tool */}
      <section style={styles.calculatorTool}>
        <div style={styles.toolContainer}>
          <div style={styles.toolHeader}>
            <h2 style={styles.h2}>{t('toolTitle')}</h2>
          </div>

          <form style={styles.calculatorForm} onSubmit={handleCalculate}>
            <div style={styles.unitTabs}>
              <button
                type="button"
                style={styles.unitTab(unitSystem === 'imperial')}
                onClick={() => setUnitSystem('imperial')}
              >
                {t('usUnits')}
              </button>
              <button
                type="button"
                style={styles.unitTab(unitSystem === 'metric')}
                onClick={() => setUnitSystem('metric')}
              >
                {t('metricUnits')}
              </button>
            </div>

            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.label}>{t('gender')}</label>
                <div style={styles.radioGroup}>
                  <label style={styles.radioLabel}>
                    <input
                      type="radio"
                      name="gender"
                      value="male"
                      checked={gender === 'male'}
                      onChange={(e) => setGender(e.target.value as 'male' | 'female')}
                      style={styles.radio}
                    />
                    {t('male')}
                  </label>
                  <label style={styles.radioLabel}>
                    <input
                      type="radio"
                      name="gender"
                      value="female"
                      checked={gender === 'female'}
                      onChange={(e) => setGender(e.target.value as 'male' | 'female')}
                      style={styles.radio}
                    />
                    {t('female')}
                  </label>
                </div>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label} htmlFor="age">{t('age')}</label>
                <input
                  type="number"
                  id="age"
                  min="18"
                  max="100"
                  value={inputs.age}
                  onChange={(e) => setInputs({...inputs, age: parseFloat(e.target.value)})}
                  style={styles.input}
                  required
                />
              </div>
            </div>

            {unitSystem === 'imperial' ? (
              <>
                <div style={styles.formRow}>
                  <div style={styles.formGroup}>
                    <label style={styles.label} htmlFor="weight-lbs">{t('weight')}</label>
                    <div style={styles.inputWrapper}>
                      <input
                        type="number"
                        id="weight-lbs"
                        min="50"
                        max="500"
                        step="0.1"
                        value={inputs.weightLbs}
                        onChange={(e) => setInputs({...inputs, weightLbs: parseFloat(e.target.value)})}
                        style={styles.input}
                        required
                      />
                      <span style={styles.unitLabel}>{t('lbs')}</span>
                    </div>
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>{t('height')}</label>
                    <div style={styles.inputWrapper}>
                      <input
                        type="number"
                        min="3"
                        max="8"
                        value={inputs.heightFt}
                        onChange={(e) => setInputs({...inputs, heightFt: parseFloat(e.target.value)})}
                        style={styles.input}
                        required
                      />
                      <span style={styles.unitLabel}>{t('ft')}</span>
                      <input
                        type="number"
                        min="0"
                        max="11"
                        step="0.1"
                        value={inputs.heightIn}
                        onChange={(e) => setInputs({...inputs, heightIn: parseFloat(e.target.value)})}
                        style={styles.input}
                        required
                      />
                      <span style={styles.unitLabel}>{t('in')}</span>
                    </div>
                  </div>
                </div>

                <div style={styles.formRow}>
                  <div style={styles.formGroup}>
                    <label style={styles.label} htmlFor="neck-in">{t('neck')}</label>
                    <div style={styles.inputWrapper}>
                      <input
                        type="number"
                        id="neck-in"
                        min="5"
                        max="30"
                        step="0.1"
                        value={inputs.neckIn}
                        onChange={(e) => setInputs({...inputs, neckIn: parseFloat(e.target.value)})}
                        style={styles.input}
                        required
                      />
                      <span style={styles.unitLabel}>{t('inches')}</span>
                    </div>
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label} htmlFor="waist-in">{t('waist')}</label>
                    <div style={styles.inputWrapper}>
                      <input
                        type="number"
                        id="waist-in"
                        min="15"
                        max="80"
                        step="0.1"
                        value={inputs.waistIn}
                        onChange={(e) => setInputs({...inputs, waistIn: parseFloat(e.target.value)})}
                        style={styles.input}
                        required
                      />
                      <span style={styles.unitLabel}>{t('inches')}</span>
                    </div>
                  </div>
                </div>

                {gender === 'female' && (
                  <div style={styles.formRow}>
                    <div style={styles.formGroup}>
                      <label style={styles.label} htmlFor="hip-in">{t('hip')}</label>
                      <div style={styles.inputWrapper}>
                        <input
                          type="number"
                          id="hip-in"
                          min="20"
                          max="80"
                          step="0.1"
                          value={inputs.hipIn}
                          onChange={(e) => setInputs({...inputs, hipIn: parseFloat(e.target.value)})}
                          style={styles.input}
                        />
                        <span style={styles.unitLabel}>{t('inches')}</span>
                      </div>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <>
                <div style={styles.formRow}>
                  <div style={styles.formGroup}>
                    <label style={styles.label} htmlFor="weight-kg">{t('weight')}</label>
                    <div style={styles.inputWrapper}>
                      <input
                        type="number"
                        id="weight-kg"
                        min="20"
                        max="250"
                        step="0.1"
                        value={inputs.weightKg}
                        onChange={(e) => setInputs({...inputs, weightKg: parseFloat(e.target.value)})}
                        style={styles.input}
                      />
                      <span style={styles.unitLabel}>{t('kg')}</span>
                    </div>
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label} htmlFor="height-cm">{t('height')}</label>
                    <div style={styles.inputWrapper}>
                      <input
                        type="number"
                        id="height-cm"
                        min="100"
                        max="250"
                        step="0.1"
                        value={inputs.heightCm}
                        onChange={(e) => setInputs({...inputs, heightCm: parseFloat(e.target.value)})}
                        style={styles.input}
                      />
                      <span style={styles.unitLabel}>{t('cm')}</span>
                    </div>
                  </div>
                </div>

                <div style={styles.formRow}>
                  <div style={styles.formGroup}>
                    <label style={styles.label} htmlFor="neck-cm">{t('neck')}</label>
                    <div style={styles.inputWrapper}>
                      <input
                        type="number"
                        id="neck-cm"
                        min="15"
                        max="80"
                        step="0.1"
                        value={inputs.neckCm}
                        onChange={(e) => setInputs({...inputs, neckCm: parseFloat(e.target.value)})}
                        style={styles.input}
                      />
                      <span style={styles.unitLabel}>{t('cm')}</span>
                    </div>
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label} htmlFor="waist-cm">{t('waist')}</label>
                    <div style={styles.inputWrapper}>
                      <input
                        type="number"
                        id="waist-cm"
                        min="40"
                        max="200"
                        step="0.1"
                        value={inputs.waistCm}
                        onChange={(e) => setInputs({...inputs, waistCm: parseFloat(e.target.value)})}
                        style={styles.input}
                      />
                      <span style={styles.unitLabel}>{t('cm')}</span>
                    </div>
                  </div>
                </div>

                {gender === 'female' && (
                  <div style={styles.formRow}>
                    <div style={styles.formGroup}>
                      <label style={styles.label} htmlFor="hip-cm">{t('hip')}</label>
                      <div style={styles.inputWrapper}>
                        <input
                          type="number"
                          id="hip-cm"
                          min="50"
                          max="200"
                          step="0.1"
                          value={inputs.hipCm}
                          onChange={(e) => setInputs({...inputs, hipCm: parseFloat(e.target.value)})}
                          style={styles.input}
                        />
                        <span style={styles.unitLabel}>{t('cm')}</span>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}

            <button type="submit" style={styles.calculateButton}>
              {t('calculateButton')}
            </button>
          </form>

          {results && (
            <div style={styles.results}>
              <h3 style={styles.h2}>{t('yourResults')}</h3>

              <div style={styles.primaryResult}>
                <div style={styles.percentage}>{results.bodyFatPercentage.toFixed(1)}%</div>
                <div style={styles.category}>{results.category}</div>
              </div>

              <div style={styles.bodyFatChart}>
                <div style={styles.chartBar}>
                  <div style={styles.chartMarker(results.markerPosition)}></div>
                </div>
                <div style={styles.chartLabels}>
                  <span>{t('chartEssential')}</span>
                  <span>{t('chartAthletes')}</span>
                  <span>{t('chartFitness')}</span>
                  <span>{t('chartAverage')}</span>
                  <span>{t('chartObese')}</span>
                </div>
              </div>

              <div style={styles.resultGrid}>
                <div style={styles.resultItem}>
                  <span style={styles.resultLabel}>{t('resultNavyMethod')}</span>
                  <span style={styles.resultValue}>{results.bodyFatPercentage.toFixed(1)}%</span>
                </div>
                <div style={styles.resultItem}>
                  <span style={styles.resultLabel}>{t('resultBodyFatMass')}</span>
                  <span style={styles.resultValue}>
                    {unitSystem === 'imperial'
                      ? `${(results.bodyFatMassKg * 2.20462).toFixed(1)} ${t('lbs')}`
                      : `${results.bodyFatMassKg.toFixed(1)} ${t('kg')}`
                    }
                  </span>
                </div>
                <div style={styles.resultItem}>
                  <span style={styles.resultLabel}>{t('resultLeanBodyMass')}</span>
                  <span style={styles.resultValue}>
                    {unitSystem === 'imperial'
                      ? `${(results.leanBodyMassKg * 2.20462).toFixed(1)} ${t('lbs')}`
                      : `${results.leanBodyMassKg.toFixed(1)} ${t('kg')}`
                    }
                  </span>
                </div>
                <div style={styles.resultItem}>
                  <span style={styles.resultLabel}>{t('resultIdealBodyFat')}</span>
                  <span style={styles.resultValue}>{results.idealBF.toFixed(1)}%</span>
                </div>
                <div style={styles.resultItem}>
                  <span style={styles.resultLabel}>{t('resultFatToLose')}</span>
                  <span style={styles.resultValue}>
                    {unitSystem === 'imperial'
                      ? `${(results.fatToLoseKg * 2.20462).toFixed(1)} ${t('lbs')}`
                      : `${results.fatToLoseKg.toFixed(1)} ${t('kg')}`
                    }
                  </span>
                </div>
                <div style={styles.resultItem}>
                  <span style={styles.resultLabel}>{t('resultBMI')}</span>
                  <span style={styles.resultValue}>{results.bmi.toFixed(1)}</span>
                </div>
              </div>

              <div style={styles.referenceTable}>
                <h4 style={{...styles.h3, marginBottom: '1rem'}}>{t('categoriesTitle')}</h4>
                <table style={styles.table}>
                  <thead>
                    <tr>
                      <th style={styles.th}>{t('tableCategory')}</th>
                      <th style={styles.th}>{t('tableWomen')}</th>
                      <th style={styles.th}>{t('tableMen')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td style={styles.td}>{t('tableEssentialFat')}</td>
                      <td style={styles.td}>10-13%</td>
                      <td style={styles.td}>2-5%</td>
                    </tr>
                    <tr>
                      <td style={styles.td}>{t('tableAthletes')}</td>
                      <td style={styles.td}>14-20%</td>
                      <td style={styles.td}>6-13%</td>
                    </tr>
                    <tr>
                      <td style={styles.td}>{t('tableFitness')}</td>
                      <td style={styles.td}>21-24%</td>
                      <td style={styles.td}>14-17%</td>
                    </tr>
                    <tr>
                      <td style={styles.td}>{t('tableAverage')}</td>
                      <td style={styles.td}>25-31%</td>
                      <td style={styles.td}>18-24%</td>
                    </tr>
                    <tr>
                      <td style={styles.td}>{t('tableObese')}</td>
                      <td style={styles.td}>32%+</td>
                      <td style={styles.td}>25%+</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Intro Section */}
      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('introTitle')}</h2>
        <p style={styles.paragraph}>{t('introParagraph1')}</p>
        <p style={styles.paragraph}>{t('introParagraph2')}</p>
        <p style={styles.paragraph}>{t('introParagraph3')}</p>
      </section>

      {/* How to Use */}
      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('howToUseTitle')}</h2>
        <ol style={styles.list}>
          <li style={styles.listItem}>{t('howToUseStep1')}</li>
          <li style={styles.listItem}>{t('howToUseStep2')}</li>
          <li style={styles.listItem}>{t('howToUseStep3')}</li>
          <li style={styles.listItem}>{t('howToUseStep4')}</li>
          <li style={styles.listItem}>{t('howToUseStep5')}</li>
          <li style={styles.listItem}>{t('howToUseStep6')}</li>
        </ol>
      </section>

      {/* Insights */}
      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('insightsTitle')}</h2>
        <p style={styles.paragraph}>{t('insightsParagraph1')}</p>
        <p style={styles.paragraph}>{t('insightsParagraph2')}</p>
        <p style={styles.paragraph}>{t('insightsParagraph3')}</p>
      </section>

      {/* Details */}
      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('detailsTitle')}</h2>
        <h3 style={styles.h3}>{t('detailsNavyMethodTitle')}</h3>
        <p style={styles.paragraph}>{t('detailsNavyMethodParagraph')}</p>

        <h3 style={styles.h3}>{t('detailsAccuracyTitle')}</h3>
        <p style={styles.paragraph}>{t('detailsAccuracyParagraph')}</p>

        <h3 style={styles.h3}>{t('detailsMethodsTitle')}</h3>
        <ul style={styles.list}>
          <li style={styles.listItem}>{t('detailsMethod1')}</li>
          <li style={styles.listItem}>{t('detailsMethod2')}</li>
          <li style={styles.listItem}>{t('detailsMethod3')}</li>
          <li style={styles.listItem}>{t('detailsMethod4')}</li>
          <li style={styles.listItem}>{t('detailsMethod5')}</li>
        </ul>
      </section>

      {/* FAQ */}
      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('faqTitle')}</h2>

        <div style={styles.faqItem}>
          <h3 style={styles.h3}>{t('faq1Question')}</h3>
          <p style={styles.paragraph}>{t('faq1Answer')}</p>
        </div>

        <div style={styles.faqItem}>
          <h3 style={styles.h3}>{t('faq2Question')}</h3>
          <p style={styles.paragraph}>{t('faq2Answer')}</p>
        </div>

        <div style={styles.faqItem}>
          <h3 style={styles.h3}>{t('faq3Question')}</h3>
          <p style={styles.paragraph}>{t('faq3Answer')}</p>
        </div>

        <div style={styles.faqItem}>
          <h3 style={styles.h3}>{t('faq4Question')}</h3>
          <p style={styles.paragraph}>{t('faq4Answer')}</p>
        </div>

        <div style={styles.faqItem}>
          <h3 style={styles.h3}>{t('faq5Question')}</h3>
          <p style={styles.paragraph}>{t('faq5Answer')}</p>
        </div>
      </section>

      {/* References */}
      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('referencesTitle')}</h2>
        <div style={styles.reference}>
          <a
            href="https://www.medicalnewstoday.com/articles/body-fat-scale-accuracy"
            target="_blank"
            rel="noopener noreferrer"
            style={styles.referenceLink}
          >
            {t('reference1')}
          </a>
        </div>
        <div style={styles.reference}>
          <a
            href="https://www.youtube.com/watch?v=fQezbeaDXIc"
            target="_blank"
            rel="noopener noreferrer"
            style={styles.referenceLink}
          >
            {t('reference2')}
          </a>
        </div>
        <div style={styles.reference}>
          <a
            href="https://www.healthline.com/health/body-fat-scale-accuracy"
            target="_blank"
            rel="noopener noreferrer"
            style={styles.referenceLink}
          >
            {t('reference3')}
          </a>
        </div>
        <div style={styles.reference}>
          <a
            href="https://www.bhf.org.uk/informationsupport/heart-matters-magazine/nutrition/weight/best-way-to-measure-body-fat"
            target="_blank"
            rel="noopener noreferrer"
            style={styles.referenceLink}
          >
            {t('reference4')}
          </a>
        </div>
        <div style={styles.reference}>
          <a
            href="https://www.houstonmethodist.org/blog/articles/2022/apr/are-body-composition-scales-accurate/"
            target="_blank"
            rel="noopener noreferrer"
            style={styles.referenceLink}
          >
            {t('reference5')}
          </a>
        </div>
      </section>
    </div>
  );
}